import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getFirestore } from 'firebase/firestore';
import { useUser } from '../components/LoginPersistence';
import { APP_VERSION, getDeployTarget } from '../config/environment';
import { WhatsNewModal } from '../components/WhatsNewModal';
import {
  hasSeenRelease,
  markReleaseSeen,
  resolveActiveReleases,
  subscribeChangelogConfig,
} from '../utils/changelogService';

const WhatsNewContext = createContext({
  previewRelease: null,
  setPreviewRelease: () => {},
});

export function useWhatsNew() {
  return useContext(WhatsNewContext);
}

export function WhatsNewProvider({ children }) {
  const { user, loading: authLoading } = useUser();
  const db = getFirestore();
  const [config, setConfig] = useState(null);
  const [visible, setVisible] = useState(false);
  const [previewRelease, setPreviewRelease] = useState(null);
  const [ready, setReady] = useState(false);
  const [seenRevision, setSeenRevision] = useState(0);
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    return subscribeChangelogConfig(db, (next) => {
      setConfig(next);
      setReady(true);
    });
  }, [db]);

  const isAdmin = user?.userType === 'admin';

  const deployTarget = getDeployTarget();

  const activeRelease = useMemo(() => {
    if (!config) return null;
    const candidates = resolveActiveReleases(config, APP_VERSION, { isAdmin, deployTarget });
    return (
      candidates.find((release) =>
        !hasSeenRelease(release, {
          userId: user?.uid,
          userSeenMap: user?.whatsNewSeen || {},
        })
      ) || null
    );
  }, [config, isAdmin, deployTarget, user?.uid, user?.whatsNewSeen, seenRevision]);

  useEffect(() => {
    if (!ready || previewRelease) return undefined;

    if (!activeRelease || authLoading || !user?.uid) {
      setVisible(false);
      hasOpenedRef.current = false;
      return undefined;
    }

    const delay = hasOpenedRef.current ? 0 : 600;
    const timer = setTimeout(() => {
      setVisible(true);
      hasOpenedRef.current = true;
    }, delay);
    return () => clearTimeout(timer);
  }, [ready, activeRelease, user?.uid, previewRelease, authLoading, seenRevision]);

  const handleDismiss = useCallback(async () => {
    if (activeRelease && user?.uid) {
      await markReleaseSeen(db, activeRelease, user.uid);
    } else if (activeRelease) {
      await markReleaseSeen(db, activeRelease, null);
    }
    setSeenRevision((n) => n + 1);
  }, [activeRelease, user?.uid, db]);

  const value = useMemo(
    () => ({ previewRelease, setPreviewRelease }),
    [previewRelease]
  );

  return (
    <WhatsNewContext.Provider value={value}>
      {children}
      <WhatsNewModal
        visible={Boolean(previewRelease) || visible}
        release={previewRelease || activeRelease}
        preview={Boolean(previewRelease)}
        onClose={() => setPreviewRelease(null)}
        onDismiss={previewRelease ? () => setPreviewRelease(null) : handleDismiss}
      />
    </WhatsNewContext.Provider>
  );
}
